import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, SparklesIcon, CheckIcon } from 'lucide-react';
import { LogoIcon } from '../components/LogoIcon';
import { CtaLink } from '../components/CtaLink';
import { ContactForm } from '../components/ContactForm';
import { PhotoTransformerPlugin } from '../components/InteriorAI/PhotoTransformerPlugin';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { interiorSteps } from '../data/interiorAI';
import { proPerks } from '../data/contact';
import { privacyUrl, contactEmail } from '../data/siteLinks';
export function InteriorAIPage() {
  const revealRef = useScrollReveal();
  return <div id="top" className="min-h-screen w-full bg-white text-apple-black font-sans selection:bg-apple-blue selection:text-white relative overflow-x-hidden" data-id="element-506" data-mp="1qyurryqp196t">
      <a href="#hlavny-obsah" className="skip-link" data-id="element-507" data-mp="fgjux132tikm">
        Preskočiť na obsah
      </a>

      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100" data-id="element-508" data-mp="kgy44mgeyyk8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-509" data-mp="qak6kf5kt9ax">
          <div className="flex justify-between items-center h-16 gap-3" data-id="element-510" data-mp="m8gvvsqqe8qp">
            <Link to="/" className="flex items-center gap-2 min-h-[44px] min-w-[44px] text-apple-gray hover:text-apple-black transition-colors rounded-lg" aria-label="Späť na nexify-studio.tech" data-id="element-511" data-mp="majrqtekew3c">
              <ArrowLeftIcon className="w-5 h-5" aria-hidden="true" data-id="element-512" data-mp="197lfgb293tk5" />
              <span className="hidden md:inline font-medium text-sm" data-id="element-513" data-mp="157b5knbc8j00">nexify-studio.tech</span>
            </Link>
            <div className="flex items-center gap-2.5" data-id="element-514" data-mp="1iyprp30jl0ba">
              <LogoIcon size={32} data-id="element-515" data-mp="1j0khaumrdglp" />
              <span className="text-base sm:text-lg font-bold tracking-tight text-apple-black" data-id="element-516" data-mp="1dyv2r50buksv">Interior AI</span>
            </div>
            <CtaLink href="#pro" size="medium" data-id="element-517" data-mp="1sxfcb3jtoyv8">
              <span className="hidden sm:inline" data-id="element-518" data-mp="1nxj936iy9bgq">Chcem Pro verziu</span>
              <span className="sm:hidden" data-id="element-519" data-mp="1nzdpq8n6uhf0">Pro</span>
            </CtaLink>
          </div>
        </div>
      </header>

      <main id="hlavny-obsah" tabIndex={-1} className="relative z-10 pt-16 focus:outline-none" ref={revealRef} data-id="element-520" data-mp="1uywhq7478rcv">
        {/* Hero */}
        <section className="pt-14 md:pt-24 pb-16 md:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-521" data-mp="24ync88srf6cy">
          <div className="reveal mb-10 md:mb-12 max-w-3xl mx-auto" data-id="element-522" data-mp="29qovieochxch">
            <h1 className="text-4xl md:text-[48px] font-bold tracking-tight text-apple-black mb-4 leading-tight" data-id="element-523" data-mp="2ep05cpen47up">
              Interior AI
            </h1>
            <p className="text-2xl md:text-[30px] font-medium text-apple-black mb-5 leading-snug" data-id="element-524" data-mp="2ioifqr8u50ja">
              Staň sa dizajnérom svojho priestoru
            </p>
            <p className="text-lg md:text-[21px] text-apple-gray leading-relaxed mb-8" data-id="element-525" data-mp="2sorcq7anipzq">
              Vytvor si vlastný dizajn interiéru domu, bytu či kancelárie — z jednej fotky, za pár sekúnd.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-526" data-mp="yb9osj6c3r46">
              <CtaLink href="#nahrat-fotku" className="w-full sm:w-auto" data-id="element-527" data-mp="qs0qpm3a3euf">
                Vyskúšať zadarmo
              </CtaLink>
              <CtaLink href="#pro" variant="secondary" className="w-full sm:w-auto" data-id="element-528" data-mp="lyxwrcula3dp">
                Chcem Pro verziu
              </CtaLink>
            </div>
          </div>

          <div className="reveal reveal-delay-1 max-w-5xl mx-auto" data-id="element-529" data-mp="c3e18rjxt6ph">
            <div className="relative rounded-2xl md:rounded-[2.5rem] overflow-hidden shadow-xl" data-id="element-530" data-mp="19qd1tddr2ysn">
              <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&h=600&fit=crop&crop=center" alt="Svetlá moderná obývačka navrhnutá pomocou Interior AI" width={1200} height={600} className="w-full h-60 sm:h-80 md:h-[560px] object-cover" data-id="element-531" data-mp="14r06e48tcjeg" />
              <span className="absolute bottom-4 left-4 md:bottom-8 md:left-8 bg-white text-apple-black text-sm font-bold px-4 py-2 rounded-full shadow-md flex items-center gap-2" data-id="element-532" data-mp="3kfs0py11az2h">
                <SparklesIcon className="w-4 h-4 text-apple-blue" aria-hidden="true" data-id="element-533" data-mp="1860c2r5o0a1t" />
                Vygenerované za 3,2 s
              </span>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="ako-to-funguje" aria-labelledby="ako-nadpis" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-apple-light/60" data-id="element-534" data-mp="y7au6ydrqplk">
          <div className="max-w-7xl mx-auto" data-id="element-535" data-mp="1qxjdwunydi29">
            <h2 id="ako-nadpis" className="reveal text-3xl md:text-[36px] font-bold tracking-tight text-apple-black text-center mb-12 md:mb-16" data-id="element-536" data-mp="1qyufqoxu8fqq">
              Ako Interior AI funguje
            </h2>
            <div className="relative" data-id="element-537" data-mp="1gzn4c7anyv69">
              <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-[2px] bg-gray-200 z-0" aria-hidden="true" data-id="element-538" data-mp="1lypxatdddaxv" />
              <ol className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 relative z-10" data-id="element-539" data-mp="5jonhh98v4uh">
                {interiorSteps.map((step, i) => <li key={step.title} className={`reveal reveal-delay-${i + 1} flex flex-col items-center text-center`} data-id="element-540" data-mp="ahgg6dhpquzq" data-mpi={i}>
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-full shadow-md flex items-center justify-center mb-6 border border-gray-100" data-id="element-541" data-mp="2jsr6mzkmp7p7">
                      <step.icon className={`w-9 h-9 ${step.iconColor}`} aria-hidden="true" data-id="element-542" data-mp="2xrh4wij6pdf5" />
                    </div>
                    <h3 className="text-xl md:text-[24px] font-bold text-apple-black mb-3" data-id="element-543" data-mp="2touaqaz1jges">{step.title}</h3>
                    <p className="text-apple-gray leading-relaxed max-w-xs" data-id="element-544" data-mp="211zszltlyv50">{step.desc}</p>
                  </li>)}
              </ol>
            </div>
          </div>
        </section>

        {/* Live demo */}
        <section id="nahrat-fotku" aria-labelledby="demo-nadpis" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-545" data-mp="2a2fskk78di4l">
          <PhotoTransformerPlugin data-id="element-546" data-mp="1mbk6m3pghh59" />
        </section>

        {/* Pro lead form */}
        <section id="pro" aria-labelledby="pro-nadpis" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-apple-light/60" data-id="element-547" data-mp="1iagmv64af0yr">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-548" data-mp="3hfzz425n138m">
            <div className="reveal lg:col-span-2" data-id="element-549" data-mp="3md9xi7b08p8l">
              <p className="text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-4" data-id="element-550" data-mp="3r5tb349ozkde">Interior AI Pro</p>
              <h2 id="pro-nadpis" className="text-3xl md:text-4xl font-bold tracking-tight text-apple-black mb-4" data-id="element-551" data-mp="3w3utro37hhoc">
                Získaj prednostný prístup
              </h2>
              <p className="text-lg text-apple-gray leading-relaxed mb-6" data-id="element-552" data-mp="33fp72vsldilv">
                Prví záujemcovia dostanú Pro verziu s 50 % zľavou.
              </p>
              <ul className="space-y-3" data-id="element-553" data-mp="38hy3y38l54t9">
                {proPerks.map(perk => <li key={perk} className="flex items-center gap-3 text-apple-black font-medium" data-id="element-554" data-mp="3dfziqbyzahyx">
                    <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-555" data-mp="nrgsqd6gnh98">
                      <CheckIcon className="w-3.5 h-3.5 text-apple-blue" aria-hidden="true" data-id="element-556" data-mp="2d6hzde63b56d" />
                    </span>
                    {perk}
                  </li>)}
              </ul>
            </div>
            <div className="reveal reveal-delay-1 lg:col-span-3 bg-white p-6 md:p-10 rounded-3xl shadow-sm border border-gray-200" data-id="element-557" data-mp="23fwuxa3qj6fj">
              <ContactForm idPrefix="pro" source="interior-ai-pro" submitLabel="Získať prednostný prístup" requireMessage={false} messageLabel="Na aký priestor chceš Interior AI použiť?" data-id="element-558" data-mp="1yh2cwadldlwh" />
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-apple-light border-t border-gray-200 pt-12 pb-8 px-4 sm:px-6 lg:px-8" data-id="element-559" data-mp="1fizifg29ywra">
        <div className="max-w-7xl mx-auto" data-id="element-560" data-mp="3evlio3otc9he">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-6" data-id="element-561" data-mp="3euip1o03vsxs">
            <div className="flex items-center gap-3" data-id="element-562" data-mp="39y2390gdpqnv">
              <LogoIcon size={32} data-id="element-563" data-mp="2iroei63zcwyj" />
              <span className="text-sm font-bold text-apple-black tracking-tight" data-id="element-564" data-mp="2isfzyo3v5eb3">Interior AI · nexify-studio.tech</span>
            </div>
            <nav aria-label="Odkazy v pätičke" className="flex flex-wrap justify-center gap-x-6 text-sm text-apple-gray" data-id="element-565" data-mp="2nskew9p4i0z7">
              <Link to="/" className="inline-flex items-center min-h-[44px] hover:text-apple-black transition-colors" data-id="element-566" data-mp="2e0eqtb1zs3pa">
                Hlavná stránka
              </Link>
              <a href="#ako-to-funguje" className="inline-flex items-center min-h-[44px] hover:text-apple-black transition-colors" data-id="element-567" data-mp="202zwbrwvt5qg">
                Ako to funguje
              </a>
              <a href="#nahrat-fotku" className="inline-flex items-center min-h-[44px] hover:text-apple-black transition-colors" data-id="element-568" data-mp="1q4aeu08wdnaq">
                Vyskúšať
              </a>
              <a href="#pro" className="inline-flex items-center min-h-[44px] hover:text-apple-black transition-colors" data-id="element-569" data-mp="3kgibpj4s4e95">
                Pro verzia
              </a>
            </nav>
          </div>
          <div className="h-px bg-gray-200 mb-6" data-id="element-570" data-mp="1drq8qypamo7h" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 text-sm text-apple-gray" data-id="element-571" data-mp="1dqez4i962m8g">
            <p data-id="element-572" data-mp="1nib329xxp6kl">© 2026 nexify-studio.tech. Všetky práva vyhradené.</p>
            <div className="flex flex-wrap justify-center gap-x-6" data-id="element-573" data-mp="1njm6g8ngwcb6">
              <a href={`mailto:${contactEmail}`} className="inline-flex items-center min-h-[44px] hover:text-apple-black transition-colors" data-id="element-574" data-mp="1iqjjijd7413y">
                {contactEmail}
              </a>
              <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center min-h-[44px] hover:text-apple-black transition-colors" data-id="element-575" data-mp="1si5qg3ii3e0w">
                Ochrana súkromia
                <span className="sr-only" data-id="element-576" data-mp="c2w8tdx40st6"> (otvorí sa v novom okne)</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}